import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixSearchV1SiteSearchServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/search-services-sitesearch',
        destPath: '/api',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/search-services-sitesearch',
        destPath: '/api',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/search-services-sitesearch',
        destPath: '/api',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/search-services-sitesearch',
        destPath: '/api',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/search-services-sitesearch',
        destPath: '/api',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/search-services-sitesearch',
        destPath: '/api',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/search-services-sitesearch',
        destPath: '/api',
      },
    ],
    _: [
      {
        srcPath: '/_api/search-services-sitesearch',
        destPath: '/api',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/sitesearch',
        destPath: '/api',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/search-services-sitesearch-server',
        destPath: '',
      },
    ],
    'apps._base_domain_': [
      {
        srcPath: '/_api/search-services-sitesearch-server',
        destPath: '/api',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_search_site-search';

/**
 * Executes a regular search query.
 * If you are unsure, this is likely the search method you want to used.
 */
export function search(payload: object): RequestOptionsFactory<any> {
  function __search({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.search.v1.sitedocument',
      method: 'POST' as any,
      methodFqn: 'wix.search.v1.SiteSearchService.Search',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSearchV1SiteSearchServiceUrl({
        protoPath: '/api/v1/search',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'facets.minAggregation.minValue' },
              { path: 'facets.maxAggregation.maxValue' },
              { path: 'facets.minMaxAggregation.minValue' },
              { path: 'facets.minMaxAggregation.maxValue' },
              { path: 'facets.sumAggregation.value' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __search;
}

/** Searches in multiple document types at once. */
export function federatedSearch(payload: object): RequestOptionsFactory<any> {
  function __federatedSearch({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.search.v1.sitedocument',
      method: 'POST' as any,
      methodFqn: 'wix.search.v1.SiteSearchService.FederatedSearch',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSearchV1SiteSearchServiceUrl({
        protoPath: '/api/v1/search/federated',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __federatedSearch;
}

/**
 * Executes search query to fetch suggested items. Unlike search query suggest will match
 * partial phrases (for example "blu" will match documents containing "blue", "blues" and "blunt").
 * Phrase needs to be at least 3 symbols long. Suggestions can also perform optimisations in search
 * results and generally do not guarantee the same level of quality as regular Search endpoint.
 */
export function suggest(payload: object): RequestOptionsFactory<any> {
  function __suggest({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.search.v1.sitedocument',
      method: 'POST' as any,
      methodFqn: 'wix.search.v1.SiteSearchService.Suggest',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSearchV1SiteSearchServiceUrl({
        protoPath: '/api/v1/suggest',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __suggest;
}

/** Searches for suggestions in multiple document types at once. */
export function federatedSuggest(payload: object): RequestOptionsFactory<any> {
  function __federatedSuggest({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.search.v1.sitedocument',
      method: 'POST' as any,
      methodFqn: 'wix.search.v1.SiteSearchService.FederatedSuggest',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSearchV1SiteSearchServiceUrl({
        protoPath: '/api/v1/suggest/federated',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __federatedSuggest;
}

/**
 * Fetches documents similar to one single document.
 * This is typically used to implement "related to" scenarios (for example to fetch related store products when
 * consumer is already viewing one).
 */
export function related(payload: object): RequestOptionsFactory<any> {
  function __related({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.search.v1.sitedocument',
      method: 'POST' as any,
      methodFqn: 'wix.search.v1.SiteSearchService.Related',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSearchV1SiteSearchServiceUrl({
        protoPath: '/api/v1/related',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __related;
}

/** Provides phrase completion. For example "blu" could return "blue", "blues" and "blunt" as candidate phrases. This operation is resource heavy at index time and is reserved for special use cases. */
export function autocomplete(payload: object): RequestOptionsFactory<any> {
  function __autocomplete({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.search.v1.sitedocument',
      method: 'POST' as any,
      methodFqn: 'wix.search.v1.SiteSearchService.Autocomplete',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSearchV1SiteSearchServiceUrl({
        protoPath: '/api/v1/autocomplete',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __autocomplete;
}

/** Provides phrase completion from multiple document types at once */
export function federatedAutocomplete(
  payload: object
): RequestOptionsFactory<any> {
  function __federatedAutocomplete({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.search.v1.sitedocument',
      method: 'POST' as any,
      methodFqn: 'wix.search.v1.SiteSearchService.FederatedAutocomplete',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSearchV1SiteSearchServiceUrl({
        protoPath: '/api/v1/autocomplete/federated',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __federatedAutocomplete;
}

/** Returns trending documents for given document types */
export function trending(payload: object): RequestOptionsFactory<any> {
  function __trending({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.search.v1.sitedocument',
      method: 'POST' as any,
      methodFqn: 'wix.search.v1.SiteSearchService.Trending',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSearchV1SiteSearchServiceUrl({
        protoPath: '/api/v1/trending',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __trending;
}
